import React, { useEffect, useState } from "react";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import { ChevronLeft, ChevronRight, User, Ban, MessageSquare, CheckCircle, RotateCcw, X } from "lucide-react";
import Tooltip from "../components/Tooltip.jsx";
import { useTopLoader } from "../context/TopLoaderContext";

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const { start, stop } = useTopLoader();

  const fetchReports = async () => {
    try {
      setLoading(true);
      start();
      const res = await api.get(URLS.GET_REPORTS, { params: { page } });
      setReports(res.data || []);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      setError("Failed to load reports.");
    } finally {
      setLoading(false);
      stop();
    }
  };

  useEffect(() => {
    fetchReports();
  }, [page]);

  const handleBlockUser = async (userId) => {
    if (!window.confirm("Are you sure you want to block this user?")) return;
    try {
      await api.delete(URLS.BLOCK_USER(userId));
      alert("User blocked successfully!");
      fetchReports();
    } catch (err) {
      alert("Failed to block user.");
    }
  };

  const handleDismissReport = async (reportId) => {
    if (!window.confirm("Dismiss this report without action?")) return;
    try {
      await api.post(URLS.DISMISS_REPORT(reportId));
      alert("Report dismissed.");
      fetchReports();
    } catch (err) {
      alert("Failed to dismiss report.");
    }
  };

  const handlePardonUser = async (userId) => {
    if (!window.confirm("Pardon this user and restore access?")) return;
    try {
      await api.post(URLS.PARDON_USER(userId));
      alert("User pardoned.");
      fetchReports();
    } catch (err) {
      alert("Failed to pardon user.");
    }
  };

  const viewMessages = async (u1, u2) => {
    try {
      setLoadingChat(true);
      setSelectedChat({ u1, u2 });
      const res = await api.get(URLS.MODERATION_MESSAGES(u1, u2));
      setChatMessages(res.data);
    } catch (err) {
      alert("Failed to load chat history.");
    } finally {
      setLoadingChat(false);
    }
  };

  return (
    <div className="p-2 sm:p-6 bg-gray-100 min-h-screen font-sans">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">User Reports & Moderation</h2>
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-x-auto border border-gray-100">
        {loading && <div className="p-8 text-center text-gray-500">Loading reports...</div>}
        {error && <div className="p-8 text-center text-red-500">{error}</div>}
        {!loading && !error && (
          <table className="min-w-[900px] w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-4 text-left text-xs font-bold text-gray-500 uppercase">Reporter</th>
                <th className="p-4 text-left text-xs font-bold text-gray-500 uppercase">Reported</th>
                <th className="p-4 text-left text-xs font-bold text-gray-500 uppercase">Reason</th>
                <th className="p-4 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                <th className="p-4 text-left text-xs font-bold text-gray-500 uppercase">Created</th>
                <th className="p-4 text-center text-xs font-bold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {reports.length > 0 ? (
                reports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img className="h-10 w-10 rounded-full object-cover border" src={ImgURL(report.reporter?.profilePhotoUrl)} alt="" />
                        <div>
                          <p className="text-gray-900 font-medium">{report.reporter?.name}</p>
                          <p className="text-gray-500 text-xs">{report.reporter?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img className="h-10 w-10 rounded-full object-cover border" src={ImgURL(report.reported?.profilePhotoUrl)} alt="" />
                        <div>
                          <p className="text-gray-900 font-medium">{report.reported?.name}</p>
                          <p className="text-gray-500 text-xs">{report.reported?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-gray-600 text-sm max-w-xs truncate">{report.reason}</td>
                    <td className="p-4 text-sm font-semibold">
                      {report.status === "BLOCKED" && <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full">Blocked</span>}
                      {report.status === "DISMISSED" && <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">Dismissed</span>}
                      {report.status === "PENDING" && <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full">Pending</span>}
                    </td>
                    <td className="p-4 text-gray-500 text-sm whitespace-nowrap">
                      {new Date(report.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-2">
                        <Tooltip text="View Chat">
                          <button onClick={() => viewMessages(report.reporter.id, report.reported.id)} className="p-2 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100"><MessageSquare size={18} /></button>
                        </Tooltip>
                        {report.status === "PENDING" && (
                          <>
                            <Tooltip text="Dismiss Report">
                              <button onClick={() => handleDismissReport(report.id)} className="p-2 bg-green-50 text-green-600 rounded-full hover:bg-green-100"><CheckCircle size={18} /></button>
                            </Tooltip>
                            <Tooltip text="Block Reported User">
                              <button onClick={() => handleBlockUser(report.reported.id)} className="p-2 bg-red-50 text-red-600 rounded-full hover:bg-red-100"><Ban size={18} /></button>
                            </Tooltip>
                          </>
                        )}
                        {report.status === "BLOCKED" && (
                          <Tooltip text="Pardon User">
                            <button onClick={() => handlePardonUser(report.reported.id)} className="p-2 bg-purple-50 text-purple-600 rounded-full hover:bg-purple-100"><RotateCcw size={18} /></button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="text-center p-8 text-gray-500">No reports to display</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Chat Modal */}
      {selectedChat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl h-[80vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <h3 className="font-bold text-xl text-gray-800 flex items-center gap-2">
                <MessageSquare className="text-[#db8654]" /> Chat History
              </h3>
              <button onClick={() => setSelectedChat(null)} className="p-2 hover:bg-gray-200 rounded-full transition"><X /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/50">
              {loadingChat ? (
                <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#db8654]"></div></div>
              ) : chatMessages.length > 0 ? (
                chatMessages.map(msg => (
                  <div key={msg.id} className={`flex flex-col ${msg.senderId === selectedChat.u1 ? 'items-start' : 'items-end'}`}>
                    <span className="text-xs text-gray-500 mb-1 font-semibold">{msg.firstName}</span>
                    <div className={`px-4 py-3 rounded-2xl max-w-[80%] text-sm shadow-sm ${msg.senderId === selectedChat.u1 ? 'bg-white text-gray-800 rounded-tl-none' : 'bg-[#db8654] text-white rounded-tr-none'}`}>
                      {msg.text}
                    </div>
                    <span className="text-[10px] text-gray-400 mt-1">{new Date(msg.createdAt).toLocaleTimeString()}</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-20 text-gray-400 font-medium">No messages found between these users.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="mt-8 flex justify-between items-center bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <button onClick={() => setPage((p) => p - 1)} disabled={page <= 1} className="px-6 py-2 rounded-xl bg-gray-50 border text-gray-700 disabled:opacity-50 hover:bg-gray-100 transition flex items-center gap-2 font-medium">
          <ChevronLeft size={18} /> Previous
        </button>
        <span className="text-gray-600 font-medium">Page {page} of {totalPages}</span>
        <button onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages} className="px-6 py-2 rounded-xl bg-gray-50 border text-gray-700 disabled:opacity-50 hover:bg-gray-100 transition flex items-center gap-2 font-medium">
          Next <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default ReportsPage;
